import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  Grid,
  Link,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import SwapHoriz from '@material-ui/icons/SwapHoriz';
import AnnounceKit from 'announcekit-react';
import { isEmpty } from 'lodash-es';
import React from 'react';

import { track } from '~/src/dash/analytics/analytics';
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import {
  FreeTrialStatusEnabled,
  getCampaign,
  IPublisher,
  IUINotice,
  UINoticeActiveTrue,
} from '~/src/dash/api/console-api';
import { getAdserverOrder, IAdserverOrder } from '~/src/dash/api/order';
import { DebugTopbar } from '~/src/dash/components/debug-topbar';
import ElevationScroll from '~/src/dash/components/model-elevation-scroll';
import { UINoticeTopbar } from '~/src/dash/components/ui-notice-topbar';
import {
  getDemoSSAIUsername,
  getPREnabled,
  getResellerTitle,
  isOtteraMainAccounts,
  isPublica,
  isRoku,
} from '~/src/dash/resellers/utils/importResellerConfig';
import {
  extractRouteParams,
  getRoutePath,
  logout,
  parseLocation,
  RouteAccountSettings,
  RouteAudienceDataShareManager,
  RouteBillingDetailList,
  RouteCampaignExisting,
  RouteIPTraceNew,
  RouteOrderList,
  RouteOrderSettingsExisting,
  RoutePublisherChangeLogs,
  RoutePublisherSettings,
  RouteTeamSettings,
} from '~/src/dash/router/routes';
import { IHistoryProps } from '~/src/dash/types/props';
import { formatDateFromUTCToOffset, parseDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import {
  backgroundColor,
  dangerCardHeader,
  primaryFreeTrialCardHeader,
  warningCardHeader,
} from '~/src/dash/utils/material-dashboard-react';
import { obfuscateCampaignName, obfuscateOrderName } from '~/src/dash/utils/obfuscate';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isMultiPublishersEnabled } from '~/src/dash/utils/ui';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';

enum HomeMode {
  Publisher = 0,
  Parent,
}

export interface IModelTopBarProps extends IHistoryProps {
  title: string;
  session: ISessionState;
  user: IUserState;
  publisher: IPublisher;
  classes: any;
  delegateElevate: boolean;
  handleChangeHomeMode: () => void;
  homeMode: HomeMode.Parent | HomeMode.Publisher;
  disableMultiPub?: boolean;
  currentPath: string;
  disableTitlePaddingLeft?: boolean;
}

interface IState {
  anchorEl: HTMLElement | undefined;
  order: IAdserverOrder;
  campaign: IAdserverCampaign;
}

export const ModelTopBar = decorate(
  {
    router: true,
    styles: () => ({
      activeBreadCrumb: {
        cursor: 'pointer',
        color: '#5D66FA',
        fontSize: 12,
      },
      inactiveBreadCrumb: {
        cursor: 'pointer',
        color: '#707070',
        fontSize: 12,
      },
      switch: {
        '&:hover': {
          background: '#eee 0% 0% no-repeat padding-box',
          borderRadius: '5px',
          textDecoration: 'none',
          color: '#555555',
          fontSize: '16px',
        },
        color: '#555555',
        textDecoration: 'none',
        fontSize: '16px',
      },
    }),
  },
  class extends React.Component<IModelTopBarProps, IState> {
    state = {
      anchorEl: undefined,
      order: {} as IAdserverOrder,
      campaign: {} as IAdserverCampaign,
    };

    componentDidMount(): void {
      this.onMenuClose = this.onMenuClose.bind(this);
      this.onAvatarClick = this.onAvatarClick.bind(this);
      this.getOrderAndCampaign();
    }

    componentDidUpdate = (prevProps: IModelTopBarProps) => {
      if (this.props.location.pathname !== prevProps.location.pathname) {
        this.getOrderAndCampaign();
      }
    };

    // if the path name has campaign and order in it, get the campaign and order
    // from api so we can use the name/ID for the breadcrumb links
    // if only order id, we only use order list and order form links
    getOrderAndCampaign = () => {
      const loc = parseLocation(this.props.location.pathname);
      const orderID = loc.order;
      const campaignID = loc.campaign;
      // if the order is in the path but not the campaign
      if (orderID && orderID !== '' && orderID !== '0' && campaignID === '') {
        getAdserverOrder(orderID, this.props.session.publisher.ID, true)
          .then((order: IAdserverOrder) => {
            this.setState({
              order,
              campaign: {} as IAdserverCampaign,
            });
          })
          .catch((err) => {
            console.error(err);
          });
        // if the order and campaign in the path
      } else if (orderID && orderID !== '' && orderID !== '0' && campaignID && campaignID !== '') {
        Promise.all([
          getAdserverOrder(orderID, this.props.session.publisher.ID, true),
          getCampaign({
            query: {
              publisher_id: this.props.session.publisher.ID,
              campaign_id: campaignID,
            },
          }),
        ])
          .then((resp) => {
            const [order, campaign] = resp;
            this.setState({
              order,
              campaign,
            });
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        this.setState({
          order: {} as IAdserverOrder,
          campaign: {} as IAdserverCampaign,
        });
      }
    };

    renderAppBarInside() {
      const { classes, currentPath, session, title, user, disableTitlePaddingLeft } = this.props;

      let freeTrialBar = null;
      const today = parseDate(
        // eslint-disable-next-line
        new Date().toLocaleString('en-US', { timeZone: session.publisher.Timezone }),
      );
      const startDay = formatDateFromUTCToOffset(
        session.publisher.FreeTrialStartDate,
        session.publisher.Timezone,
      );
      const endDay = formatDateFromUTCToOffset(
        session.publisher.FreeTrialEndDate,
        session.publisher.Timezone,
      );
      const numDaysRemaining = endDay.diff(today, 'days');
      const hoursFromStart = today.diff(startDay, 'hours');

      let tooltipText = `You are currently in Trial Mode. You have ${numDaysRemaining} days left before your trial expires. Please reach out to your ${getResellerTitle()} contact if you have any questions!`;
      let color = primaryFreeTrialCardHeader.background;
      if (numDaysRemaining === 2) {
        tooltipText = `You only have ${numDaysRemaining} days remaining on your free trial. Please reach out to your ${getResellerTitle()} contact to avoid any interruption in service.`;
        color = warningCardHeader.background;
      } else if (numDaysRemaining === 1) {
        tooltipText = `You only have ${numDaysRemaining} day remaining on your free trial. Please reach out to your ${getResellerTitle()} contact to avoid any interruption in service.`;
        color = warningCardHeader.background;
      } else if (numDaysRemaining <= 0) {
        tooltipText = `Your free trial has ended, your account is now in read-only mode. Please reach out to your ${getResellerTitle()} representative to continue to optimize your OTT revenue!`;
        color = dangerCardHeader.background;
      }
      if (session.publisher.FreeTrialActive === FreeTrialStatusEnabled && hoursFromStart >= 0) {
        freeTrialBar = (
          <div style={{ width: '180px', paddingTop: '5px' }}>
            <Box display='flex' width='100%' alignItems='center' paddingRight='10px'>
              <Typography
                style={{
                  color: '#292F36', // Page header color
                  fontWeight: 400,
                  fontSize: '1rem',
                }}
                variant='h6'
                className={classes.appbarTitle}
              >
                Free Trial
              </Typography>
              <Tooltip title={tooltipText}>
                <Chip
                  label={`${numDaysRemaining >= 0 ? numDaysRemaining : 0} ${
                    numDaysRemaining === 1 ? `day` : `days`
                  } left`}
                  style={{ background: color, color: 'white' }}
                />
              </Tooltip>
            </Box>
          </div>
        );
      }

      const betaChip = (
        <Chip
          label={'Beta'}
          color='primary'
          style={{ fontSize: '1rem', color: 'white', margin: '0.75rem' }}
        />
      );

      const isDashboardView =
        currentPath.indexOf('dashboards') > -1 || currentPath.indexOf('network-dashboard');
      const isReportingView = currentPath.indexOf('bidder-reports') > -1;
      const uiNotices = isEmpty(user.uiNotices)
        ? []
        : user.uiNotices.filter((uiNotice: IUINotice) => uiNotice.Active === UINoticeActiveTrue);
      return (
        <AppBar
          className={classes.appbar}
          style={{
            backgroundColor,
            marginLeft: 0,
            position: 'relative',
          }}
          elevation={0}
        >
          <>
            {getPREnabled() && <DebugTopbar />}

            {/* UI Notice */}
            {(isDashboardView || isReportingView) &&
              !checkUserInfoMasked(user.user) &&
              !isEmpty(uiNotices) && (
                <UINoticeTopbar
                  uiNotices={uiNotices}
                  currentPath={currentPath}
                  publisherID={session.publisher.ID}
                />
              )}
            <Toolbar
              style={{
                paddingLeft: title.indexOf('Dashboard') > -1 || disableTitlePaddingLeft ? 0 : 24,
                justifyContent: 'space-between',
                position: 'relative',
              }}
            >
              {this.renderTitle()}

              <div style={{ display: 'flex' }}>
                {isRoku() &&
                  session.publisher.FreeTrialActive !== FreeTrialStatusEnabled &&
                  betaChip}
                {freeTrialBar}
                {((isPublica() && getDemoSSAIUsername() !== user.user.Username) ||
                  isOtteraMainAccounts(session.publisher.ID)) && (
                  <div style={{ marginRight: '20px' }}>
                    {/* @ts-ignore */}
                    <AnnounceKit widget='https://announcekit.co/widgets/v2/dINjO'>
                      <div style={{ height: '32px', lineHeight: '32px' }}>
                        <img
                          id='announce-widget-publica-icon'
                          style={{ display: 'inline-block' }}
                        />
                      </div>
                    </AnnounceKit>
                  </div>
                )}

                <Avatar onClick={(e) => this.onAvatarClick(e)}>
                  {user.user.FirstName.charAt(0)}
                </Avatar>
                <Menu
                  anchorEl={this.state.anchorEl}
                  open={!!this.state.anchorEl}
                  onClose={this.onMenuClose}
                >
                  <MenuItem>
                    <Link
                      style={{
                        textDecoration: 'none',
                        color: 'black',
                        display: 'inline-block',
                        width: '100%',
                      }}
                      href={
                        '#' +
                        getRoutePath(
                          RoutePublisherSettings,
                          extractRouteParams({
                            session,
                            user,
                          }),
                        )
                      }
                    >
                      General Settings
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link
                      style={{
                        textDecoration: 'none',
                        color: 'black',
                        display: 'inline-block',
                        width: '100%',
                      }}
                      href={
                        '#' +
                        getRoutePath(
                          RouteAccountSettings,
                          extractRouteParams({
                            session,
                            user,
                          }),
                        )
                      }
                    >
                      Account Settings
                    </Link>
                  </MenuItem>
                  {hasRoleSuperUser(user.user) && (
                    <MenuItem>
                      <Link
                        style={{
                          textDecoration: 'none',
                          color: 'black',
                          display: 'inline-block',
                          width: '100%',
                        }}
                        href={
                          '#' +
                          getRoutePath(
                            RouteBillingDetailList,
                            extractRouteParams({
                              session,
                              user,
                            }),
                          )
                        }
                      >
                        Billing Details *
                      </Link>
                    </MenuItem>
                  )}
                  {hasRoleSuperUser(user.user) && (
                    <MenuItem>
                      <Link
                        style={{
                          textDecoration: 'none',
                          color: 'black',
                          display: 'inline-block',
                          width: '100%',
                        }}
                        href={
                          '#' +
                          getRoutePath(
                            RouteAudienceDataShareManager,
                            extractRouteParams({
                              session,
                              user,
                            }),
                          )
                        }
                      >
                        Audience Data Share *
                      </Link>
                    </MenuItem>
                  )}
                  <MenuItem>
                    <Link
                      style={{
                        textDecoration: 'none',
                        color: 'black',
                        display: 'inline-block',
                        width: '100%',
                      }}
                      href={
                        '#' +
                        getRoutePath(
                          RoutePublisherChangeLogs,
                          extractRouteParams({
                            session,
                            user,
                          }),
                        )
                      }
                    >
                      Change Logs
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link
                      style={{
                        textDecoration: 'none',
                        color: 'black',
                        display: 'inline-block',
                        width: '100%',
                      }}
                      href={
                        '#' +
                        getRoutePath(
                          RouteIPTraceNew,
                          extractRouteParams({
                            session,
                            user,
                          }),
                        )
                      }
                    >
                      IP Trace
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link
                      style={{
                        textDecoration: 'none',
                        color: 'black',
                        display: 'inline-block',
                        width: '100%',
                      }}
                      href={
                        '#' +
                        getRoutePath(
                          RouteTeamSettings,
                          extractRouteParams({
                            session,
                            user,
                          }),
                        )
                      }
                    >
                      Team
                    </Link>
                  </MenuItem>
                  <MenuItem onClick={logout}>Logout</MenuItem>
                </Menu>
              </div>
            </Toolbar>
          </>
        </AppBar>
      );
    }

    renderTitle() {
      // parse location
      const { handleChangeHomeMode, publisher, classes } = this.props;
      const { order, campaign } = this.state;

      let orderName = 'Unknown';
      if (order.Name && order.ID) {
        orderName = obfuscateOrderName(order, this.props.user.user.Username, order.Name);
      }

      let campaignName = 'Unknown';
      if (campaign.Name && campaign.ID) {
        campaignName = obfuscateCampaignName(
          campaign,
          this.props.user.user.Username,
          campaign.Name,
        );
      }

      return (
        <Grid
          item
          container
          direction='row'
          justifyContent='flex-start'
          alignItems='center'
          spacing={6}
        >
          <Grid item>
            <Typography
              style={{
                color: '#292F36', // Page header color
                fontWeight: 500,
              }}
              variant='h5'
              className={`${classes.appbarTitle} pb-model-topbar_title_bar`}
            >
              {this.props.title}
            </Typography>
            {order.Name &&
              campaign.Name && ( // navigates with order list, order form and campaign form
                <Grid item>
                  <Breadcrumbs aria-label='breadcrumb'>
                    <Link
                      href={'#' + getRoutePath(RouteOrderList, extractRouteParams(this.props))}
                      className={classes.inactiveBreadCrumb}
                    >
                      All Orders
                    </Link>
                    <Link
                      href={
                        '#' +
                        getRoutePath(RouteOrderSettingsExisting, extractRouteParams(this.props), {
                          order: (this.state.order.ID || 0).toString(),
                        })
                      }
                      className={classes.inactiveBreadCrumb}
                    >
                      {orderName}
                    </Link>
                    <Link
                      href={
                        '#' +
                        getRoutePath(RouteCampaignExisting, extractRouteParams(this.props), {
                          c: (this.state.campaign.ID || 0).toString(),
                          order: (this.state.order.ID || 0).toString(),
                        })
                      }
                      className={`${classes.activeBreadCrumb} pb-model-topbar-line_item_link`}
                    >
                      {campaignName}
                    </Link>
                  </Breadcrumbs>
                </Grid>
              )}
            {order.Name &&
              campaign.Name === undefined && ( // navigates with order list, order form
                <Grid item>
                  <Breadcrumbs>
                    <Link
                      href={'#' + getRoutePath(RouteOrderList, extractRouteParams(this.props))}
                      className={classes.inactiveBreadCrumb}
                    >
                      All Orders
                    </Link>
                    <Link
                      href={
                        '#' +
                        getRoutePath(RouteOrderSettingsExisting, extractRouteParams(this.props), {
                          order: (this.state.order.ID || 0).toString(),
                        })
                      }
                      className={classes.inactiveBreadCrumb}
                    >
                      {orderName}
                    </Link>
                  </Breadcrumbs>
                </Grid>
              )}
          </Grid>
          {this.props.title === 'Measurement Dashboard' && (
            <Tooltip
              title={`All data provided in dashboards below has been collected and analyzed by IAS's proprietary measurement technologies`}
            >
              <Card
                style={{
                  boxShadow: '0px 0px 0px',
                  backgroundColor: '#F8FAFF',
                }}
              >
                <Grid container direction='row'>
                  <img
                    src='/assets/shared/svg/IASLogo.svg'
                    style={{ width: '25px', marginRight: '6px' }}
                  />
                  <Typography
                    style={{
                      fontSize: '0.8rem',
                      color: '#00324A',
                      fontWeight: 500,
                    }}
                  >
                    Certified Data
                  </Typography>
                </Grid>
              </Card>
            </Tooltip>
          )}
          {publisher &&
            this.props.title === 'Dashboard' &&
            isMultiPublishersEnabled(publisher) &&
            !this.props.disableMultiPub && (
              <Grid
                item
                container
                direction='row'
                justifyContent='flex-start'
                alignItems='center'
                spacing={3}
                style={{ width: '50%' }}
              >
                <Tooltip
                  title={
                    this.props.homeMode === HomeMode.Publisher
                      ? `Switch to parent view`
                      : `Switch to ${publisher.Name} view`
                  }
                >
                  <Button
                    onClick={handleChangeHomeMode}
                    className={classes.switch}
                    style={{ textTransform: 'none' }}
                  >
                    <Grid>
                      {this.props.homeMode === HomeMode.Publisher
                        ? `${publisher.Name} View`
                        : 'Parent View'}
                    </Grid>
                    <Grid item>
                      <SwapHoriz
                        fontSize='small'
                        color='disabled'
                        style={{
                          marginTop: '5px',
                          marginLeft: '5px',
                          color: '#263652',
                        }}
                      />
                    </Grid>
                  </Button>
                </Tooltip>
              </Grid>
            )}
        </Grid>
      );
    }

    render() {
      if (this.props.delegateElevate === true) {
        return this.renderAppBarInside();
      }

      return <ElevationScroll {...this.props}>{this.renderAppBarInside()}</ElevationScroll>;
    }

    onMenuClose() {
      this.setState({
        anchorEl: undefined,
      });
    }

    onAvatarClick({ currentTarget }: React.MouseEvent<HTMLElement>) {
      this.setState({
        anchorEl: currentTarget,
      });
      track(
        'Header - Management Settings',
        { isSuperUser: hasRoleSuperUser(this.props.user.user) },
        this.props.user.user,
      );
    }
  },
);
